import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Scale, Gavel, ScrollText, Loader2, WifiOff, User, FileText, Banknote, Timer, Stamp } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PreviewRibbon, DEMO, NcdbDisclaimer } from "@/components/cef/cefShared";
import CourtDesk from "@/components/cef/CourtDesk";

/* Courthouse surface (CEF). /cef/court/:serverId?token=<player-session>
   Closes the case lifecycle: CAD alert -> arrest -> booking -> plea -> hearing -> verdict -> fine/jail.
   Defendants plea from here; judges (judge-role sessions / bridge) call hearings and deliver verdicts.
   No token → demo docket so owners/devs can preview the bench. */

const SEV_CLS = { infraction: "text-zinc-400 border-white/15", misdemeanor: "text-nc-orange border-nc-orange/50", felony: "text-red-400 border-red-500/50" };
const STATUS_CLS = { awaiting_hearing: "text-nc-orange border-nc-orange/50", in_hearing: "text-nc-blue border-nc-blue/50 bg-nc-blue/10", verdict: "text-nc-green border-nc-green/50", warrant: "text-red-400 border-red-500/50" };
const fmt = (n) => `$${Number(n ?? 0).toLocaleString("en-AU")}`;

export default function CourtCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [sel, setSel] = useState(null); // full case doc
  const [busy, setBusy] = useState(false);
  const [verdict, setVerdict] = useState({ outcome: "guilty", fine: "", jail_seconds: "", notes: "", judge_name: "" });
  const [view, setView] = useState("docket"); // docket | desk

  const load = useCallback(() => {
    if (demo) { setData(DEMO.court); setErr(""); return; }
    api.get(`/nc/servers/${serverId}/court/docket`, { params: { token } })
      .then((r) => { setData(r.data); setErr(""); })
      .catch((e) => setErr(e.response?.data?.detail || "Courthouse offline"));
  }, [demo, serverId, token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const openCase = (id) => {
    if (demo) { setSel(DEMO.courtCase[id] || null); return; }
    api.get(`/nc/servers/${serverId}/court/cases/${id}`, { params: { token } })
      .then((r) => setSel(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  };

  const act = async (path, body, refreshCase) => {
    if (demo) { toast.info("Preview mode — pleas and verdicts bind in-game via player sessions"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${serverId}/court/cases/${sel.id}/${path}`, body, { params: { token } });
      toast.success(r.data.message);
      load();
      if (refreshCase) openCase(sel.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const viewer = data?.viewer || {};
  const isDefendant = sel && viewer.playercode === sel.defendant?.playercode;
  const canJudge = viewer.is_judge;

  return (
    <div data-testid="cef-court" className="fixed inset-0 bg-[#0a0805] text-zinc-100 flex flex-col" style={{ backgroundImage: "radial-gradient(ellipse at 50% -20%, rgba(255,107,26,0.08) 0%, transparent 60%)" }}>
      {demo && <PreviewRibbon what="the courthouse" />}
      <NcdbDisclaimer />
      {/* bench header */}
      <div className="flex items-center gap-3 px-8 py-5 border-b border-white/10" style={{ marginTop: demo ? 26 : 0 }}>
        <Scale className="w-5 h-5 text-nc-orange" />
        <div>
          <h1 className="font-sans font-bold uppercase tracking-[0.35em] text-base">District Courthouse</h1>
          <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.25em]">Criminal division · session live</p>
        </div>
        <div className="flex border border-white/15 ml-4">
          {[["docket", ScrollText, "Docket"], ["desk", Stamp, "Warrant desk"]].map(([v, Icon, l]) => (
            <button key={v} data-testid={`court-view-${v}`} onClick={() => setView(v)}
              className={`font-mono text-[9px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 flex items-center gap-1.5 transition-colors ${view === v ? "bg-nc-orange/20 text-nc-orange" : "text-zinc-500 hover:text-zinc-200"}`}>
              <Icon className="w-3 h-3" />{l}
            </button>
          ))}
        </div>
        <span className="ml-auto font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
          {viewer.is_judge ? "Bench access" : viewer.playercode ? `Citizen #${viewer.playercode}` : "Public gallery"}
        </span>
      </div>

      {view === "desk" && <CourtDesk serverId={serverId} token={token} demo={demo} />}

      {err && <div className="flex-1 flex items-center justify-center gap-3 flex-col"><WifiOff className="w-6 h-6 text-red-400" /><p data-testid="court-error" className="font-mono text-[11px] text-red-400">{err}</p></div>}
      {!data && !err && <div className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-nc-orange" /></div>}

      {data && view === "docket" && (
        <div className="flex-1 flex gap-6 p-6 min-h-0">
          {/* docket */}
          <div className="w-[380px] shrink-0 flex flex-col gap-2 overflow-y-auto">
            <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase">Today's docket ({data.cases.length})</p>
            {data.cases.length === 0 && <p data-testid="court-empty" className="font-mono text-[10px] text-zinc-600 mt-6 text-center">No cases on the docket — the city sleeps easy.</p>}
            {data.cases.map((c) => (
              <button key={c.id} data-testid={`docket-${c.docket}`} onClick={() => openCase(c.id)}
                className={`text-left border p-3.5 transition-colors ${sel?.id === c.id ? "border-nc-orange/60 bg-nc-orange/5" : "border-white/10 hover:border-white/30"}`}>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] text-nc-orange">{c.docket}</span>
                  <span className={`font-mono text-[8px] uppercase border px-1.5 py-0.5 ${STATUS_CLS[c.status]}`}>{c.status.replace(/_/g, " ")}</span>
                </div>
                <p className="font-sans font-semibold text-sm mt-1.5">The City v. {c.defendant?.name}</p>
                <p className="font-mono text-[9px] text-zinc-500 mt-0.5">{c.charges.length} charge{c.charges.length === 1 ? "" : "s"}{c.plea ? ` · plea: ${c.plea.replace(/_/g, " ")}` : ""}</p>
              </button>
            ))}
          </div>

          {/* case detail */}
          <div className="flex-1 overflow-y-auto">
            {!sel ? (
              <div className="h-full flex items-center justify-center"><p className="font-mono text-[10px] text-zinc-600 uppercase tracking-[0.3em]">Select a case from the docket</p></div>
            ) : (
              <div data-testid="court-case" className="flex flex-col gap-5 max-w-3xl">
                <div>
                  <p className="font-mono text-[10px] text-nc-orange">{sel.docket}{sel.arrest_ref ? ` · from ${sel.arrest_ref}` : ""}</p>
                  <h2 className="font-sans font-bold text-2xl mt-1">The City v. {sel.defendant?.name}</h2>
                  <p className="font-mono text-[9px] text-zinc-500 uppercase mt-1">Arresting officer: {sel.arresting_officer || "—"} · Citizen #{sel.defendant?.playercode}</p>
                </div>

                {/* charges */}
                <div>
                  <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase mb-2 flex items-center gap-1.5"><ScrollText className="w-3 h-3" /> Charges</p>
                  {sel.charges.map((ch, i) => (
                    <div key={i} data-testid={`charge-${i}`} className="border border-white/10 px-4 py-3 flex items-center gap-3 flex-wrap mb-1.5">
                      <span className={`font-mono text-[8px] uppercase border px-1.5 py-0.5 ${SEV_CLS[ch.severity]}`}>{ch.severity}</span>
                      <span className="font-sans text-sm">{ch.title}</span>
                      <span className="ml-auto font-mono text-[9px] text-zinc-500 flex items-center gap-3">
                        {ch.fine > 0 && <span className="flex items-center gap-1"><Banknote className="w-3 h-3" />{fmt(ch.fine)}</span>}
                        {ch.jail_seconds > 0 && <span className="flex items-center gap-1"><Timer className="w-3 h-3" />{Math.round(ch.jail_seconds / 60)}min</span>}
                      </span>
                    </div>
                  ))}
                </div>

                {sel.evidence_summary && (
                  <div>
                    <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase mb-2 flex items-center gap-1.5"><FileText className="w-3 h-3" /> Evidence summary</p>
                    <p className="font-mono text-[10px] text-zinc-400 leading-relaxed border border-white/10 bg-black/30 p-4">{sel.evidence_summary}</p>
                  </div>
                )}

                {(sel.interviews || []).length > 0 && (
                  <div>
                    <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase mb-2 flex items-center gap-1.5"><User className="w-3 h-3" /> Witness testimony (NCDB)</p>
                    {sel.interviews.map((iv, i) => (
                      <div key={i} className="border border-white/10 bg-black/30 p-4 mb-1.5">
                        <p className="font-mono text-[10px] text-nc-blue">{iv.npc_name}</p>
                        {(iv.clues_revealed || []).map((c, j) => <p key={j} className="font-mono text-[9px] text-zinc-400 mt-1">▸ {c}</p>)}
                        {(iv.clues_revealed || []).length === 0 && <p className="font-mono text-[9px] text-zinc-600 mt-1">Interviewed — no admissions on record.</p>}
                      </div>
                    ))}
                  </div>
                )}

                {/* plea */}
                {data.court_style === "corrupt" && sel.status !== "verdict" && sel.defendant?.playercode === viewer.playercode && (
                  <div data-testid="court-bribe" className="border border-nc-orange/40 bg-nc-orange/5 p-4 mb-3 flex flex-col gap-2">
                    <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-nc-orange">The clerk is… approachable</p>
                    <p className="font-mono text-[9.5px] text-zinc-400 leading-snug">
                      ${(Math.round(sel.charges.reduce((a, c) => a + (c.fine || 0), 0) * 1.5)).toLocaleString()} in an envelope, and your paperwork mysteriously disappears. No judge, no record.
                    </p>
                    <button data-testid="court-bribe-btn" disabled={busy} onClick={async () => {
                      if (demo) { toast.success("The clerk pockets the envelope. Paperwork misfiled — dismissed. (demo)"); return; }
                      setBusy(true);
                      try { const r = await api.post(`/nc/servers/${serverId}/court/cases/${sel.id}/bribe`, {}, { params: { token } }); toast.success(r.data.message); load(); setSel(null); }
                      catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                      finally { setBusy(false); }
                    }} className="w-fit font-mono text-[9px] uppercase tracking-[0.2em] border border-nc-orange/60 text-nc-orange px-4 py-2 hover:bg-nc-orange/10 transition-colors disabled:opacity-40">
                      Slip the envelope — dismiss my case
                    </button>
                  </div>
                )}
                <div className="border border-white/10 p-4 flex items-center gap-3 flex-wrap">
                  <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Plea:</p>
                  <span data-testid="plea-display" className="font-mono text-[10px] uppercase text-zinc-200">{sel.plea ? sel.plea.replace(/_/g, " ") : "not entered"}</span>
                  {isDefendant && ["awaiting_hearing", "in_hearing"].includes(sel.status) && (
                    <span className="ml-auto flex gap-1.5">
                      {["not_guilty", "no_contest", "guilty"].map((p) => (
                        <button key={p} data-testid={`plea-${p}`} disabled={busy} onClick={() => act("plea", { plea: p }, true)}
                          className={`font-mono text-[8px] uppercase border px-3 py-2 transition-colors disabled:opacity-40 ${p === "guilty" ? "border-red-500/50 text-red-400 hover:bg-red-500/10" : "border-nc-blue/50 text-nc-blue hover:bg-nc-blue/10"}`}>
                          {p.replace(/_/g, " ")}
                        </button>
                      ))}
                    </span>
                  )}
                </div>

                {/* verdict */}
                {sel.status === "verdict" && sel.verdict ? (
                  <div data-testid="verdict-display" className={`border p-5 ${sel.verdict.outcome === "guilty" ? "border-red-500/40 bg-red-500/5" : "border-nc-green/40 bg-nc-green/5"}`}>
                    <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-1.5"><Gavel className="w-3 h-3" /> Verdict — {sel.verdict.judge || "the court"}, {String(sel.verdict.at).slice(0, 16).replace("T", " ")}</p>
                    <p className="font-sans font-bold text-xl mt-2 uppercase">{sel.verdict.outcome.replace(/_/g, " ")}</p>
                    <p className="font-mono text-[10px] text-zinc-300 mt-1.5">
                      {sel.verdict.fine > 0 && <>Fine {fmt(sel.verdict.fine)} (debited via city bank) · </>}
                      {sel.verdict.jail_seconds > 0 && <>{Math.round(sel.verdict.jail_seconds / 60)} minutes custody</>}
                      {!sel.verdict.fine && !sel.verdict.jail_seconds && "No penalty imposed"}
                    </p>
                    {sel.verdict.notes && <p className="font-mono text-[9px] text-zinc-500 mt-2">{sel.verdict.notes}</p>}
                  </div>
                ) : canJudge && (
                  <div data-testid="verdict-form" className="border border-nc-orange/30 bg-nc-orange/5 p-4 flex flex-col gap-3">
                    <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-nc-orange flex items-center gap-1.5"><Gavel className="w-3 h-3" /> Bench controls</p>
                    {sel.status === "awaiting_hearing" && (
                      <button data-testid="hearing-start" disabled={busy} onClick={() => act("hearing", {}, true)} className="font-mono text-[9px] uppercase border border-nc-blue/50 text-nc-blue px-4 py-2.5 hover:bg-nc-blue/10 transition-colors w-fit">Call hearing to order</button>
                    )}
                    {sel.status === "in_hearing" && (
                      <>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <select data-testid="verdict-outcome" value={verdict.outcome} onChange={(e) => setVerdict({ ...verdict, outcome: e.target.value })} className="bg-black border border-white/15 px-2 py-2.5 font-mono text-[11px]">
                            <option value="guilty">Guilty</option><option value="not_guilty">Not guilty</option><option value="dismissed">Dismissed</option>
                          </select>
                          <input data-testid="verdict-fine" inputMode="numeric" placeholder="Fine $" value={verdict.fine} onChange={(e) => setVerdict({ ...verdict, fine: e.target.value.replace(/\D/g, "") })} className="bg-black border border-white/15 px-3 py-2.5 font-mono text-[11px] focus:outline-none focus:ring-1 focus:ring-nc-orange" />
                          <input data-testid="verdict-jail" inputMode="numeric" placeholder="Custody (sec)" value={verdict.jail_seconds} onChange={(e) => setVerdict({ ...verdict, jail_seconds: e.target.value.replace(/\D/g, "") })} className="bg-black border border-white/15 px-3 py-2.5 font-mono text-[11px] focus:outline-none focus:ring-1 focus:ring-nc-orange" />
                          <input data-testid="verdict-judge" placeholder="Judge name" value={verdict.judge_name} maxLength={80} onChange={(e) => setVerdict({ ...verdict, judge_name: e.target.value })} className="bg-black border border-white/15 px-3 py-2.5 font-mono text-[11px] focus:outline-none focus:ring-1 focus:ring-nc-orange" />
                        </div>
                        <input data-testid="verdict-notes" placeholder="Bench notes (optional)" value={verdict.notes} maxLength={1000} onChange={(e) => setVerdict({ ...verdict, notes: e.target.value })} className="bg-black border border-white/15 px-3 py-2.5 font-mono text-[11px] focus:outline-none focus:ring-1 focus:ring-nc-orange" />
                        <button data-testid="verdict-deliver" disabled={busy} onClick={() => act("verdict", { ...verdict, fine: Number(verdict.fine || 0), jail_seconds: Number(verdict.jail_seconds || 0) }, true)}
                          className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-mono text-[10px] uppercase tracking-[0.2em] px-5 py-3 transition-colors w-fit flex items-center gap-2">
                          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gavel className="w-4 h-4" />} Deliver Verdict
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
